import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiError, blobApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { FileCheck2, FileDown, FileText, Image as ImageIcon, Loader2, Trash2, Truck, Upload } from "lucide-react";

const LABELS = ["POD", "Invoice", "Photo", "Other"];

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function DeliveryDocs({ quoteId, gtzBol }) {
  const [docs, setDocs] = useState([]);
  const [label, setLabel] = useState("POD");
  const [busy, setBusy] = useState(false);
  const [podBusy, setPodBusy] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/quotes/${quoteId}/docs`).then((r) => setDocs(r.data)).catch(() => {});
  }, [quoteId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label);
      await api.post(`/quotes/${quoteId}/docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${label} uploaded`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const view = async (d) => {
    try {
      const res = await api.get(`/quotes/${quoteId}/docs/${d.id}`, { responseType: "blob" });
      const url = globalThis.URL.createObjectURL(new Blob([res.data], { type: d.content_type }));
      globalThis.open(url, "_blank", "noopener");
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (d) => {
    try {
      await api.delete(`/quotes/${quoteId}/docs/${d.id}`);
      toast.success("Document removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportPdf = async () => {
    try {
      const res = await api.get(`/quotes/${quoteId}/pod-audit/pdf`, { responseType: "blob" });
      const url = globalThis.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      globalThis.open(url, "_blank", "noopener");
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const fetchTrifectaPod = async () => {
    setPodBusy(true);
    try {
      const res = await api.get(`/quotes/${quoteId}/gtz-pod/pdf`, { responseType: "blob" });
      const url = globalThis.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      globalThis.open(url, "_blank", "noopener");
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(await blobApiError(e));
    } finally {
      setPodBusy(false);
    }
  };

  return (
    <div className="border border-border bg-card" data-testid="delivery-docs">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <FileCheck2 className="h-4 w-4 text-[hsl(var(--primary))]" />
        <span className="font-heading text-base font-bold">Delivery Docs</span>
        <span className="text-xs text-muted-foreground">signed POD, invoices, photos</span>
        <div className="ml-auto flex items-center gap-2">
          {gtzBol && (
            <Button size="sm" variant="outline" onClick={fetchTrifectaPod} disabled={podBusy} data-testid="gtz-pod-fetch-btn" className="h-8 gap-1.5"
              title="Pull the carrier's signed POD from the Trifecta / GlobalTranz portal">
              {podBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Truck className="h-3.5 w-3.5" />} Trifecta POD
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={exportPdf} data-testid="pod-export-pdf-btn" className="h-8 gap-1.5">
            <FileDown className="h-3.5 w-3.5" /> Export PDF
          </Button>
          <select value={label} onChange={(e) => setLabel(e.target.value)} data-testid="docs-label-select"
            className="h-8 rounded-md border border-border bg-background px-2 text-xs font-semibold">
            {LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <input ref={inputRef} type="file" accept="application/pdf,image/*" className="hidden"
            onChange={(e) => upload(e.target.files?.[0])} data-testid="docs-file-input" />
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={busy} data-testid="docs-upload-btn" className="h-8 gap-1.5">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
          </Button>
        </div>
      </div>
      <div className="p-4">
        {docs.length === 0 ? (
          <p className="py-3 text-center text-sm text-muted-foreground" data-testid="docs-empty">
            No documents yet — attach the signed POD after delivery so billing disputes are one click away.
          </p>
        ) : (
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2" data-testid={`doc-row-${d.id}`}>
                {d.content_type === "application/pdf"
                  ? <FileText className="h-4 w-4 shrink-0 text-red-400" />
                  : <ImageIcon className="h-4 w-4 shrink-0 text-blue-400" />}
                <button onClick={() => view(d)} className="min-w-0 flex-1 text-left" data-testid={`doc-view-${d.id}`}>
                  <div className="truncate text-sm font-semibold hover:underline">{d.filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.uploaded_by} · {new Date(d.uploaded_at).toLocaleString()} · {fmtSize(d.size)}
                  </div>
                </button>
                <span className="rounded bg-[hsl(var(--primary))]/15 px-1.5 py-0.5 text-[10px] font-bold text-[hsl(var(--primary))]">{d.label}</span>
                <button onClick={() => remove(d)} className="rounded p-1 text-muted-foreground transition-colors hover:text-red-400" title="Delete" data-testid={`doc-delete-${d.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
